import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Toggle, Select, ImageUpload, RichTextEditor, Spinner } from '../../components/common';
import { blogService } from '../../services/blogService';
import { getApiError } from '../../services/cmsService';
import ReviewFormInput from '../reviews/ReviewFormInput';
import { useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import { formatDateForInput } from '../../utils/dateUtils';
import './BlogFormPage.css';

const BlogFormPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEditing = Boolean(slug);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  const validationRules = {
    title: [validators.required, validators.minLength(5), validators.maxLength(200)],
    excerpt: [validators.required, validators.maxLength(300)],
    category: [validators.required],
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    validate
  } = useForm({
    title: '',
    excerpt: '',
    content: '',
    coverImage: null,
    category: '',
    metaTitle: '',
    metaDescription: '',
    publishDate: formatDateForInput(new Date()),
    status: 'draft'
  }, validationRules);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await blogService.getCategories();
      setCategories(response.data.map(cat => ({ value: cat, label: cat })));
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  }, []);

  const fetchBlog = useCallback(async () => {
    try {
      const response = await blogService.getBySlug(slug);
      const data = response.data;
      setValues({
        title: data.title || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        coverImage: data.coverImage || null,
        category: data.category || '',
        metaTitle: data.metaTitle || '',
        metaDescription: data.metaDescription || '',
        publishDate: formatDateForInput(data.publishDate) || formatDateForInput(new Date()),
        status: data.status || 'draft'
      });
    } catch (error) {
      toast.error('Failed to fetch blog');
      navigate('/blogs');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate, setValues]);

  useEffect(() => {
    fetchCategories();
    if (isEditing && slug) {
      fetchBlog();
    }
  }, [isEditing, slug, fetchCategories, fetchBlog]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the form errors');
      return;
    }
    
    setSaving(true);
    
    try {
      // ImageUpload hands back { file, preview } for a new upload; an untouched
      // existing cover is still its URL string, and a cleared one is null.
      const formData = {
        ...values,
        coverImage: values.coverImage?.file || values.coverImage
      };
      
      if (isEditing) {
        await blogService.update(slug, formData);
        toast.success('Blog updated successfully');
      } else {
        await blogService.create(formData);
        toast.success('Blog created successfully');
      }
      
      navigate('/blogs');
    } catch (error) {
      toast.error(getApiError(error, isEditing ? 'Failed to update blog' : 'Failed to create blog'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (e) => {
    setFieldValue('status', e.target.checked ? 'published' : 'draft');
  };

  const handleFillDemo = () => {
    setValues({
      title: 'Structural Design Trends 2026',
      excerpt: 'Latest industry trends in structural design and engineering for 2026.',
      content: '<p>As structural engineering evolves, new trends are reshaping the way we design and build. From advanced materials to digital twins, 2026 marks a pivotal year for the industry.</p><p>Key trends include parametric design, sustainable construction methods, and the integration of AI in structural analysis.</p>',
      coverImage: values.coverImage,
      category: 'Engineering',
      metaTitle: 'Structural Design Trends 2026 | Design & Infra',
      metaDescription: 'Explore the latest structural design and engineering trends shaping infrastructure in 2026.',
      publishDate: '2026-02-24',
      status: 'published'
    });
    toast.info('Demo data filled — review and click Save.');
  };

  if (loading) {
    return (
      <div className="form-loading">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="blog-form-page">
      <div className="form-header">
        <Button
          variant="ghost"
          icon={<FiArrowLeft />}
          onClick={() => navigate('/blogs')}
        >
          Back to Blogs
        </Button>
        <h2>{isEditing ? 'Edit Blog' : 'Add Blog'}</h2>
        {!isEditing && (
          <Button
            type="button"
            variant="outline"
            icon={<FiZap />}
            onClick={handleFillDemo}
          >
            Fill Demo Blog
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-main">
            <Card>
              <div className="form-section form-input-field">
                <ReviewFormInput
                  name="title"
                  label="Title"
                  placeholder="Enter blog title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.title}
                  touched={touched.title}
                  required
                  className="form-text-input"
                />
              </div>

              <div className="form-section form-input-field">
                <ReviewFormInput
                  name="excerpt"
                  label="Excerpt"
                  placeholder="Enter a brief excerpt"
                  value={values.excerpt}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.excerpt}
                  touched={touched.excerpt}
                  required
                  className="form-text-input"
                />
              </div>

              <div className="form-section">
                <RichTextEditor
                  label="Content"
                  value={values.content}
                  onChange={(content) => setFieldValue('content', content)}
                  placeholder="Write your blog content here..."
                />
              </div>
            </Card>

            <Card title="SEO Settings">
              <div className="form-section form-input-field">
                <ReviewFormInput
                  name="metaTitle"
                  label="Meta Title"
                  placeholder="Enter meta title for SEO"
                  value={values.metaTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className="form-section form-input-field">
                <ReviewFormInput
                  name="metaDescription"
                  label="Meta Description"
                  placeholder="Enter meta description for SEO"
                  value={values.metaDescription}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
            </Card>
          </div>

          <div className="form-sidebar">
            <Card title="Publish">
              <div className="publish-section">
                <Toggle
                  name="status"
                  checked={values.status === 'published'}
                  onChange={handleStatusChange}
                  label={values.status === 'published' ? 'Published' : 'Draft'}
                />
              </div>
              <div className="form-section">
                <ReviewFormInput
                  type="date"
                  name="publishDate"
                  label="Publish Date"
                  value={values.publishDate}
                  onChange={handleChange}
                />
              </div>
              <div className="publish-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/blogs')}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  icon={<FiSave />}
                  loading={saving}
                  fullWidth
                >
                  {isEditing ? 'Update' : 'Save'}
                </Button>
              </div>
            </Card>

            <Card title="Category">
              <Select
                name="category"
                value={values.category}
                onChange={handleChange}
                onBlur={handleBlur}
                options={categories}
                placeholder="Select category"
                error={errors.category}
                touched={touched.category}
                required
              />
            </Card>

            <Card title="Cover Image">
              <ImageUpload
                value={values.coverImage}
                onChange={(image) => setFieldValue('coverImage', image)}
                label=""
              />
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogFormPage;
